/**
 * The funnel's output: **pre-filled forms, not silently created events.**
 *
 * One input can carry one event (a typed line) or a season's worth (a flyer, an itinerary PDF, a
 * pasted schedule). Either way the funnel batch-fills a create-event form per event it extracted
 * and hands the set to the user, who can confirm the whole batch with one tap when it got things
 * right, or open any single form and correct it when it didn't. That is the error model:
 * **best guess, easy correction** — the funnel commits to an interpretation and the pre-filled
 * form is the correction surface.
 *
 * Flags exist to tell the user *which* forms deserve that second look — extractions the reader
 * was unsure of, and events that look like duplicates. They inform review; they never gate
 * creation. A deliberate confirm-all creates every form in the set as-is, low confidence and
 * unresolved duplicates included; the safety net afterwards is the app-wide undo stack (see the
 * `history` domain).
 *
 * Pure policy, in the shape of its neighbours: the model proposes the extractions (ADR 0011),
 * this core disposes — deriving the flags, the headline, and exactly what confirming commits.
 */

import { type EventDraft, type EventProblem, validateEvent } from '@project/event-format';

import {
  type IntakeDuplicate,
  type IntakeDuplicateResolution,
  type IntakeExistingEvent,
  findIntakeDuplicate,
} from './duplicates';
import type { IntakeMedia } from './media';

/**
 * Below this the extraction is flagged for a look. Best judgement until there's data on how the
 * readers actually calibrate — the same honest-constant stance as the confidence rubric in
 * `@project/inference` and ADR 0009.
 */
export const INTAKE_CONFIDENCE_BAR = 0.7;

/** One event the reader pulled out of the input, with how sure it was. */
export interface IntakeExtraction {
  readonly draft: EventDraft;
  /** `0…1` from the reader. Anything under {@link INTAKE_CONFIDENCE_BAR} gets flagged. */
  readonly confidence: number;
  /** What the reader supplied that the input didn't say ("assumed one hour"). */
  readonly assumptions?: readonly string[];
  readonly assumptionCodes?: readonly (
    'all-day' | 'day-month' | 'inherited-date' | 'missing-title'
  )[];
}

/** Why a form is worth a second look. Exactly two reasons — both informational. */
export type IntakeFlag =
  | { readonly kind: 'low-confidence'; readonly note: string }
  | { readonly kind: 'duplicate'; readonly note: string; readonly duplicateOf: IntakeDuplicate };

/** One pre-filled create-event form in the batch. */
export interface IntakeForm {
  readonly id: string;
  readonly draft: EventDraft;
  readonly confidence: number;
  readonly assumptions: readonly string[];
  readonly assumptionCodes?: IntakeExtraction['assumptionCodes'];
  /** The same validation gate the form itself uses — shown, never used to drop the event. */
  readonly problems: readonly EventProblem[];
  readonly flags: readonly IntakeFlag[];
  /** Set once the user has decided what to do with a flagged duplicate. */
  readonly resolution?: IntakeDuplicateResolution;
}

/** Everything one trip through the funnel produced. */
export interface IntakeBatch {
  /**
   * Which trip through the funnel this is. Form ids restart at `form-1` every time, so anything
   * that outlives the batch — an undo entry, say — has to name the batch too, or it will land on
   * a same-numbered form belonging to something else entirely. Caller-supplied, because this core
   * has no clock and no randomness of its own.
   */
  readonly id: string;
  readonly media: IntakeMedia;
  readonly forms: readonly IntakeForm[];
  /** Ids of the forms carrying at least one flag, in batch order. */
  readonly flagged: readonly string[];
  /**
   * Ids of forms that have left the batch **having produced an event** — confirmed by an earlier
   * partial commit. A later form that merges into one of these folds into it; a form that left
   * because the user skipped it produced nothing, so a merge into *that* has to create instead.
   */
  readonly settled: readonly string[];
  /** The line the review surface leads with. */
  readonly headline: string;
}

export interface BuildIntakeBatchInput {
  /** Identifies this trip through the funnel; defaults to `batch`, which suits a single-shot use. */
  readonly id?: string;
  readonly media: IntakeMedia;
  readonly extractions: readonly IntakeExtraction[];
  /** Calendar events to check the extractions against. Omit to skip the calendar check. */
  readonly existing?: readonly IntakeExistingEvent[];
  /** Override the flagging bar; defaults to {@link INTAKE_CONFIDENCE_BAR}. */
  readonly confidenceBar?: number;
}

const formId = (index: number): string => `form-${index + 1}`;

function duplicateNote(duplicate: IntakeDuplicate): string {
  return duplicate.scope === 'batch'
    ? `Looks like another event in this batch — "${duplicate.title}"`
    : `Looks like an event already on your calendar — "${duplicate.title}"`;
}

function headlineFor(total: number, flagged: number, media: IntakeMedia): string {
  if (media.kind === 'unsupported') return "I can't read that one — add the event by hand";
  if (total === 0) return "I couldn't find an event in that — add it by hand";
  const events = `${total} ${total === 1 ? 'event' : 'events'} filled in`;
  if (flagged === 0) return `${events} — they all look right`;
  return `${events} — ${flagged} worth a look`;
}

/**
 * Turn what the reader extracted into the batch of forms the user reviews.
 *
 * Every extraction becomes a form, including ones that fail validation: a mostly-right event the
 * user fixes in two taps beats a dropped one they have to retype. Forms keep the order they were
 * extracted in, so the second copy of a duplicated pair is the one flagged.
 */
export function buildIntakeBatch(input: BuildIntakeBatchInput): IntakeBatch {
  const bar = input.confidenceBar ?? INTAKE_CONFIDENCE_BAR;
  const existing = input.existing ?? [];
  const forms: IntakeForm[] = [];

  input.extractions.forEach((extraction, index) => {
    const flags: IntakeFlag[] = [];
    if (extraction.confidence < bar) {
      flags.push({ kind: 'low-confidence', note: "I wasn't sure I read this one right" });
    }
    const duplicate = findIntakeDuplicate(extraction.draft, forms, existing);
    if (duplicate !== undefined) {
      flags.push({ kind: 'duplicate', note: duplicateNote(duplicate), duplicateOf: duplicate });
    }
    forms.push({
      id: formId(index),
      draft: extraction.draft,
      confidence: extraction.confidence,
      assumptions: extraction.assumptions ?? [],
      ...(extraction.assumptionCodes === undefined
        ? {}
        : { assumptionCodes: extraction.assumptionCodes }),
      problems: validateEvent(extraction.draft),
      flags,
    });
  });

  const flagged = forms.filter((form) => form.flags.length > 0).map((form) => form.id);
  return {
    id: input.id ?? 'batch',
    media: input.media,
    forms,
    flagged,
    settled: [],
    headline: headlineFor(forms.length, flagged.length, input.media),
  };
}

function replaceForm(batch: IntakeBatch, id: string, next: (form: IntakeForm) => IntakeForm) {
  const forms = batch.forms.map((form) => (form.id === id ? next(form) : form));
  const flagged = forms.filter((form) => form.flags.length > 0).map((form) => form.id);
  return {
    ...batch,
    forms,
    flagged,
    headline: headlineFor(forms.length, flagged.length, batch.media),
  };
}

/**
 * Write a user's edit back into the batch.
 *
 * Editing re-runs validation and clears the low-confidence flag — the user has now read that
 * form, which is all the flag was ever asking for. A duplicate flag survives an edit, because
 * resolving it is an explicit choice ({@link resolveIntakeDuplicate}), not something an edit to
 * the title should silently decide.
 */
export function updateIntakeForm(batch: IntakeBatch, id: string, draft: EventDraft): IntakeBatch {
  return replaceForm(batch, id, (form) => ({
    ...form,
    draft,
    problems: validateEvent(draft),
    flags: form.flags.filter((flag) => flag.kind !== 'low-confidence'),
  }));
}

/**
 * The next free `form-N`, so a second trip through the funnel cannot land on the first one's ids.
 *
 * Derived from what is actually there rather than from a count: the user can delete a form, and
 * numbering from `forms.length` would then hand a later arrival an id a survivor already has.
 */
function nextFormNumber(forms: readonly IntakeForm[]): number {
  let highest = 0;
  for (const form of forms) {
    const match = /^form-(\d+)$/.exec(form.id);
    if (match !== null) highest = Math.max(highest, Number(match[1]));
  }
  return highest + 1;
}

/**
 * Add what another trip through the funnel found to what the user has already collected.
 *
 * The funnel is one box people use more than once — a pasted fixture list, then a flyer, then a
 * line they typed. Replacing the batch each time would throw away the events they just gathered
 * (and any corrections they made to them), so runs accumulate instead, and a form leaves only
 * when the user says so.
 *
 * Three things this has to get right, none of them free:
 *
 * - **Ids.** `buildIntakeBatch` numbers from `form-1` every time, so the incoming forms are
 *   renumbered past the collection. Anything naming a form by id — a duplicate flag pointing at
 *   an earlier form in the same run — is rewritten through the same map, or it would come to
 *   rest on a form belonging to someone else's run.
 * - **Duplicates across runs.** The incoming batch was flagged against its own forms and the
 *   calendar; it could not see what was already collected. The same flyer read twice is exactly
 *   what a user does, so each arrival is checked against the collection too.
 * - **The batch identity.** The collection keeps its own `id` and `settled`, because undo entries
 *   recorded before an append name that batch and have to keep finding it.
 *
 * An incoming batch with no forms changes nothing: it is a read that found no events, which the
 * surface reports in its own words rather than by emptying what the user has.
 */
export function appendIntakeBatch(collected: IntakeBatch, incoming: IntakeBatch): IntakeBatch {
  if (incoming.forms.length === 0) return collected;

  let next = nextFormNumber(collected.forms);
  const renamed = new Map<string, string>();
  for (const form of incoming.forms) renamed.set(form.id, `form-${next++}`);

  const forms: IntakeForm[] = [...collected.forms];
  for (const form of incoming.forms) {
    const flags = form.flags.map((flag) =>
      flag.kind === 'duplicate' && flag.duplicateOf.scope === 'batch'
        ? {
            ...flag,
            duplicateOf: {
              ...flag.duplicateOf,
              id: renamed.get(flag.duplicateOf.id) ?? flag.duplicateOf.id,
            },
          }
        : flag,
    );
    // One duplicate flag per form, as `buildIntakeBatch` does: a form already pointing at
    // something is not made clearer by pointing at a second thing as well.
    const across = flags.some((flag) => flag.kind === 'duplicate')
      ? undefined
      : findIntakeDuplicate(form.draft, forms, []);
    forms.push({
      ...form,
      id: renamed.get(form.id) ?? form.id,
      flags:
        across === undefined
          ? flags
          : [...flags, { kind: 'duplicate', note: duplicateNote(across), duplicateOf: across }],
    });
  }

  const flagged = forms.filter((form) => form.flags.length > 0).map((form) => form.id);
  // The collection's media is whichever one the headline is about. Once there are forms in it,
  // that is what was already there; the first run to fill it names it.
  const media = collected.forms.length > 0 ? collected.media : incoming.media;
  return {
    ...collected,
    media,
    forms,
    flagged,
    headline: headlineFor(forms.length, flagged.length, media),
  };
}

/**
 * Take a form out because the user removed it — not because it produced anything.
 *
 * The distinction from {@link dropIntakeForms} is `settled`: a form that left by being confirmed
 * left an event behind, and a later duplicate merging into it folds into that event. A deleted
 * form left nothing, so it must not be recorded as having landed — a copy merging into it would
 * otherwise fold into an event that was never created and quietly write nothing at all.
 *
 * A duplicate flag naming the deleted form goes with it. The flag says "looks like another event
 * in this batch", and once that event is gone the sentence is not true — leaving it behind would
 * ask the user to resolve a duplicate of nothing.
 */
export function removeIntakeForm(batch: IntakeBatch, id: string): IntakeBatch {
  const forms = batch.forms
    .filter((form) => form.id !== id)
    .map((form) => {
      const dangling = form.flags.some(
        (flag) =>
          flag.kind === 'duplicate' &&
          flag.duplicateOf.scope === 'batch' &&
          flag.duplicateOf.id === id,
      );
      if (!dangling) return form;
      const flags = form.flags.filter(
        (flag) => !(flag.kind === 'duplicate' && flag.duplicateOf.id === id),
      );
      // Rebuilt without the key rather than set to undefined: a form with nothing left to resolve
      // should not still be showing the chip the user picked for a duplicate that has gone.
      const cleared: IntakeForm = {
        id: form.id,
        draft: form.draft,
        confidence: form.confidence,
        assumptions: form.assumptions,
        problems: form.problems,
        flags,
      };
      return cleared;
    });

  const flagged = forms.filter((form) => form.flags.length > 0).map((form) => form.id);
  return {
    ...batch,
    forms,
    flagged,
    headline: headlineFor(forms.length, flagged.length, batch.media),
  };
}

/**
 * Put forms back where they were — the inverse of {@link removeIntakeForm}, and what undoing a
 * removal is made of.
 *
 * An inverse rather than a restored snapshot, because a snapshot of the batch would also undo
 * everything that arrived *after* the removal: the funnel accumulates, so "put that one back"
 * has to mean that one, not "wind the list back to how it looked". Each entry is placed by id
 * where one exists and inserted at its recorded index where it does not — which covers both
 * halves of a removal, the row itself and the forms whose duplicate flags named it.
 *
 * Entries are applied in ascending index order, so several inserts land where they were rather
 * than shifting each other along.
 */
export function restoreIntakeForms(
  batch: IntakeBatch,
  entries: readonly { readonly index: number; readonly form: IntakeForm }[],
): IntakeBatch {
  const forms = [...batch.forms];
  for (const entry of [...entries].sort((a, b) => a.index - b.index)) {
    const at = forms.findIndex((form) => form.id === entry.form.id);
    if (at >= 0) forms[at] = entry.form;
    else forms.splice(Math.min(entry.index, forms.length), 0, entry.form);
  }
  const flagged = forms.filter((form) => form.flags.length > 0).map((form) => form.id);
  return {
    ...batch,
    forms,
    flagged,
    headline: headlineFor(forms.length, flagged.length, batch.media),
  };
}

/**
 * Take forms out of the batch — what's left after confirming only some of them. The user is still
 * reviewing the rest, so the batch keeps going with a headline that counts what remains.
 *
 * `landed` names the departing forms that actually produced an event; the rest were skipped.
 * That distinction outlives them: a form still in the batch may merge into one of them, and
 * folding into something that was skipped would write nothing at all. Defaults to all of `ids`.
 */
export function dropIntakeForms(
  batch: IntakeBatch,
  ids: readonly string[],
  landed?: readonly string[],
): IntakeBatch {
  const forms = batch.forms.filter((form) => !ids.includes(form.id));
  const flagged = forms.filter((form) => form.flags.length > 0).map((form) => form.id);
  return {
    ...batch,
    forms,
    flagged,
    // Deduped: repeated partial confirms of overlapping sets would otherwise grow this forever.
    settled: [...new Set([...batch.settled, ...(landed ?? ids)])],
    headline: headlineFor(forms.length, flagged.length, batch.media),
  };
}

/**
 * Record what the user chose to do with a flagged duplicate — or, with `undefined`, put the form
 * back to undecided, which is what undoing their first choice means. Leaving a resolution behind
 * would show a selected chip the user never picked.
 */
export function resolveIntakeDuplicate(
  batch: IntakeBatch,
  id: string,
  resolution: IntakeDuplicateResolution | undefined,
): IntakeBatch {
  return replaceForm(batch, id, (form) => {
    if (resolution !== undefined) return { ...form, resolution };
    // Rebuilt without the key, so an undecided form has no resolution rather than an undefined
    // one — the difference a `toHaveProperty` check (and a rendered chip) can see.
    const undecided: IntakeForm = {
      id: form.id,
      draft: form.draft,
      confidence: form.confidence,
      assumptions: form.assumptions,
      ...(form.assumptionCodes === undefined ? {} : { assumptionCodes: form.assumptionCodes }),
      problems: form.problems,
      flags: form.flags,
    };
    return undecided;
  });
}

/** One event to create, and the form it came from. */
export interface IntakeCreate {
  readonly formId: string;
  readonly draft: EventDraft;
}

/** One existing event to overwrite, and the form that asked for it. */
export interface IntakeMerge {
  readonly formId: string;
  readonly eventId: string;
  readonly draft: EventDraft;
}

/**
 * What confirming the batch actually commits.
 *
 * Every write names the form it came from, so a caller can tell afterwards which forms were dealt
 * with — including after a write the calendar refused half-way, where "the first two landed" has
 * to become "these two forms are done" without counting positions in a list.
 */
export interface IntakeCommit {
  /** New events to create, in batch order. */
  readonly create: readonly IntakeCreate[];
  /** Existing calendar events to update in place, from duplicates the user chose to merge. */
  readonly merge: readonly IntakeMerge[];
  /** Ids of forms the user chose to skip. */
  readonly skipped: readonly string[];
}

/**
 * Confirm the batch — the whole set, or just the forms named in `ids`.
 *
 * **Confirm-all wins over all flags.** A form that is merely flagged is created as-is; only an
 * explicit per-duplicate resolution changes that: `skip` drops it, `merge` updates the calendar
 * event it duplicates instead of creating a second one, `keep` creates it.
 *
 * A `merge` on a duplicate of an *earlier form in this same batch* has no calendar event to
 * update, so it folds into that earlier form — but **only if that form is actually creating the
 * event this time round**. Confirming the second copy alone, or skipping the first, would
 * otherwise fold the event into something that never happens and quietly create nothing.
 *
 * A form that left the batch in an earlier partial confirm folds only if it produced an event
 * then (`settled`); one that left by being skipped produced nothing, so the copy is created.
 */
export function confirmIntakeBatch(batch: IntakeBatch, ids?: readonly string[]): IntakeCommit {
  const chosen = ids === undefined ? batch.forms : batch.forms.filter((f) => ids.includes(f.id));
  const create: IntakeCreate[] = [];
  const merge: IntakeMerge[] = [];
  const skipped: string[] = [];

  // Which forms leave an event behind — created outright, or merged into one that already
  // exists. A batch-scope merge folds into an earlier form only if that form's event will be
  // there afterwards; forms are in batch order and a batch duplicate always points at an earlier
  // one, so the answer is known by the time it's needed.
  const landing = new Set<string>();
  // Forms an earlier partial confirm turned into real events. A merge into one of those still
  // folds — otherwise the user picks "Merge" and gets a second event — while a merge into a form
  // that left by being skipped has nothing to fold into and must create.
  const settled = new Set(batch.settled);

  for (const form of chosen) {
    const duplicate = form.flags.find(
      (flag): flag is Extract<IntakeFlag, { kind: 'duplicate' }> => flag.kind === 'duplicate',
    )?.duplicateOf;
    const merging = form.resolution === 'merge';
    if (form.resolution === 'skip') {
      skipped.push(form.id);
    } else if (merging && duplicate?.scope === 'calendar') {
      merge.push({ formId: form.id, eventId: duplicate.id, draft: form.draft });
      landing.add(form.id);
    } else if (
      merging &&
      duplicate !== undefined &&
      (landing.has(duplicate.id) || settled.has(duplicate.id))
    ) {
      skipped.push(form.id);
    } else {
      create.push({ formId: form.id, draft: form.draft });
      landing.add(form.id);
    }
  }

  return { create, merge, skipped };
}
